import { useContext, useState } from 'react';
import {
  Button,
  Text,
  Flex,
  Divider,
  Alert,
  AlertIcon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from '@chakra-ui/react';
import { useBilling } from '../../../store/client/BillingContext';
import { HttpContext } from '../../../context/HttpContext';
import CustomToast from '../../../common/CustomToast';
import { useStripe } from '@stripe/react-stripe-js';

const ResubscribeModal = ({ isOpen, handleClose, onSuccess, subscription }) => {
  const { authAxios } = useContext(HttpContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { paymentMethod, updateSubscription } = useBilling();
  const stripe = useStripe();
  const toast = useToast();
  const handleResubscribe = async () => {
    try {
      const isCanceled = subscription?.canResubscribe;
      const apiUrl = `/internal/v1/billing/subscriptions/prepare_resubscribe`;

      const confirmationUrl = `/internal/v1/billing/subscriptions/confirm_resubscribe`;

      const currentInterval = subscription?.plan?.interval || 'month';
      const matchingPrice =
        subscription?.plan?.prices?.find(
          (p) => p.interval === currentInterval
        ) || subscription?.plan?.prices?.[0];
      const apiData = {
        price_id: matchingPrice?.id,
      };

      if (isCanceled && paymentMethod?.id) {
        apiData.payment_method_id = paymentMethod.id;
      }

      let response;
      try {
        response = await authAxios.post(apiUrl, apiData);
      } catch (apiError) {
        console.error('API call failed with error:', apiError);
        throw apiError;
      }

      const {
        data: {
          requires_action: requiresAction,
          client_secret: clientSecret,
          payment_method_id: paymentMethodId,
          stripe_subscription_id: stripeSubscriptionId,
        },
      } = response;

      if (requiresAction) {
        await stripe
          .confirmPayment({
            clientSecret,
            confirmParams: {
              payment_method: paymentMethodId,
              return_url: window.location.href,
            },
            redirect: 'if_required',
          })
          .then(async (result) => {
            response = await authAxios.post(confirmationUrl, {
              success: !result.error,
              stripe_subscription_id: stripeSubscriptionId,
              error: result.error,
            });
          });
      } else {
        response = await authAxios.post(confirmationUrl, {
          success: true,
          stripe_subscription_id: stripeSubscriptionId,
        });
      }

      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Subscription updated successfully"
            description=""
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });

      const { subscription: updatedSubscription } = response.data;

      if (updatedSubscription) {
        updateSubscription(updatedSubscription);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors[0];

      if (errorMessage) {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Error"
              description={errorMessage}
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      } else {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Error"
              description="Something went wrong"
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      }
    }
  };

  const handleUpdatePlan = async () => {
    setLoading(true);
    setError(null);

    const planId = subscription?.plan?.stripeProductId;
    const currentInterval = subscription?.plan?.interval || 'month';
    const priceId =
      subscription?.plan?.prices?.find((p) => p.interval === currentInterval)
        ?.id || subscription?.plan?.prices?.[0]?.id;

    if (!planId) {
      setError('Missing required plan ID');
      setLoading(false);
      return;
    }

    if (!priceId) {
      setError('Missing required price ID');
      setLoading(false);
      return;
    }

    if (handleResubscribe) {
      try {
        await handleResubscribe();
        setLoading(false);
        onSuccess();
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent pt={6} px={4}>
        <ModalBody mb={4}>
          <Text textStyle="heading3" textAlign="center">
            Reactivate your account?
          </Text>
          <Divider border="1px solid" borderColor="lightGrey" mt="10px" />
          <Flex direction="column" px={6} mt={6}>
            <Text textstyle="bodyLarge" fontSize="16px">
              Reactivating will unlock your organisation and restore access to
              your plan features.
            </Text>
            <Text textStyle="bodyLarge" mt={2} fontSize="16px">
              You’ll be charged now on your previous plan and can update or
              switch plans at any time.
            </Text>
          </Flex>
          {error && (
            <Flex px={6} mt={6}>
              <Alert status="error" borderRadius="5px">
                <AlertIcon />
                {error}
              </Alert>
            </Flex>
          )}
          <Flex
            direction="column"
            gap={6}
            w="full"
            justifyContent="center"
            mt="30px"
          >
            <Button
              data-testid="confirm-update-plan-button"
              onClick={handleUpdatePlan}
              isLoading={loading}
              loadingText="Processing"
              w="100%"
            >
              <Text textStyle="bodySmall">Confirm</Text>
            </Button>
            <Button variant="link" onClick={handleClose} w="100%">
              <Text textStyle="bodySmall">Cancel</Text>
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ResubscribeModal;
